/* Adds quick cart actions to product listings without changing product detail links. */
(() => {
  'use strict';
  const productCards = '.product-card, .listing-card';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const base = root.endsWith('/') ? root : root + '/';
  const cache = new Map();

  function productHandle(card) {
    const handle = card.getAttribute('data-live-product-handle');
    if (handle) return handle;
    const link = [...card.querySelectorAll('a[href]')].find(a => /\/products\/[^/?#]+/.test(a.getAttribute('href') || ''));
    if (!link) return '';
    const match = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    try { return match ? decodeURIComponent(match[1]) : ''; } catch (_) { return ''; }
  }

  async function getProduct(handle) {
    if (cache.has(handle)) return cache.get(handle);
    const response = await fetch(base + 'products/' + encodeURIComponent(handle) + '.js', {credentials:'same-origin'});
    if (!response.ok) throw new Error('Product unavailable');
    const product = await response.json();
    cache.set(handle, product);
    return product;
  }

  async function refreshCartCount() {
    try {
      const response = await fetch(base + 'cart.js', {credentials:'same-origin'});
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; });
    } catch (_) {}
  }

  async function addVariant(variant, button, status) {
    button.disabled = true;
    status.textContent = '';
    try {
      const response = await fetch(base + 'cart/add.js', {
        method:'POST', credentials:'same-origin',
        headers:{'Content-Type':'application/json','Accept':'application/json'},
        body:JSON.stringify({items:[{id:variant.id,quantity:1}]})
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Could not add to cart');
      status.textContent = 'Added to cart ✓';
      await refreshCartCount();
      if (window.MomoLiveInventory && window.MomoLiveInventory.refresh) window.MomoLiveInventory.refresh(true);
    } catch (error) {
      status.textContent = error.message || 'Could not add to cart';
    } finally {
      button.disabled = false;
    }
  }

  async function quickAdd(card, button, status) {
    const handle = productHandle(card);
    if (!handle) return;
    button.disabled = true;
    status.textContent = '';
    try {
      const product = await getProduct(handle);
      const available = (product.variants || []).filter(v => v.available);
      if (!available.length) {
        button.textContent = 'Sold out';
        button.disabled = true;
        return;
      }
      if (product.variants.length === 1) {
        await addVariant(available[0], button, status);
        return;
      }
      // Do not silently pick a language/pack/edition when choices are available.
      let picker = card.querySelector('[data-momo-quick-picker]');
      if (picker) { picker.hidden = !picker.hidden; return; }
      picker = document.createElement('div');
      picker.className = 'momo-quick-picker';
      picker.setAttribute('data-momo-quick-picker', '');
      const select = document.createElement('select');
      select.setAttribute('aria-label', 'Choose product option');
      available.forEach(v => {
        const option = document.createElement('option');
        option.value = v.id;
        option.textContent = v.public_title || v.title || 'Default';
        select.appendChild(option);
      });
      const choose = document.createElement('button');
      choose.type = 'button';
      choose.className = 'btn full momo-quick-confirm';
      choose.textContent = 'Add selected to cart';
      choose.addEventListener('click', async () => {
        const variant = available.find(v => String(v.id) === select.value);
        if (variant) await addVariant(variant, choose, status);
        picker.hidden = true;
      });
      picker.append(select, choose);
      button.insertAdjacentElement('afterend', picker);
    } catch (_) {
      status.textContent = 'Unable to add right now. Please use View Product.';
    } finally {
      if (button.textContent !== 'Sold out') button.disabled = false;
    }
  }

  // Video Add-on is rendered as a custom page/card, not a regular product card.
  // Only add an actual Shopify variant. Never report success without a cart response.
  async function addVideo(button) {
    const scope = button.closest('.product-card, .detail-copy') || button.parentElement;
    const status = scope.querySelector('[data-momo-video-feedback]');
    button.disabled = true;
    status.textContent = '';
    try {
      let product;
      for (const handle of ['video-add-on', 'video-addon', 'video-add-on-free', 'video-request']) {
        try { product = await getProduct(handle); if (product) break; } catch (_) {}
      }
      if (!product) throw new Error('Video Add-on is not set up as a Shopify product yet. Create a free product named Video Add-on with handle video-add-on.');
      const variants = (product.variants || []).filter(v => v.available);
      if (!variants.length) throw new Error('Video Add-on is currently unavailable.');
      if (variants.length > 1) throw new Error('Video Add-on has multiple options. Please configure one default variant.');
      await addVariant(variants[0], button, status);
    } catch (error) {
      status.textContent = error.message || 'Could not add Video Add-on to cart.';
      button.disabled = false;
    }
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-momo-video-add]').forEach(button => {
      if (button.dataset.videoCartReady) return;
      button.dataset.videoCartReady = 'true';
      button.addEventListener('click', () => addVideo(button));
    });
    scope.querySelectorAll(productCards).forEach(card => {
      if (card.querySelector('[data-momo-quick-add]')) return;
      if (!productHandle(card)) return;
      const view = [...card.querySelectorAll('a.btn')].find(a => /view product|voir le produit/i.test(a.textContent));
      if (!view) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'btn full momo-quick-add';
      button.textContent = 'Add to Cart';
      button.setAttribute('data-momo-quick-add', '');
      const status = document.createElement('span');
      status.className = 'momo-quick-feedback';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      view.insertAdjacentElement('beforebegin', button);
      view.insertAdjacentElement('afterend', status);
      button.addEventListener('click', () => quickAdd(card, button, status));
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
